import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, NgForm, Validators } from '@angular/forms';
import { ACLService } from '@delon/acl';
import { SettingsService, _HttpClient } from '@delon/theme';
import { copy } from '@delon/util';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { FormService } from 'src/app/core/util/form.service';
import { ReportingService } from 'src/app/core/util/reporting.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-invoice-email-modal',
  templateUrl: './invoice-email-modal.component.html',
  styleUrls: ['./invoice-email-modal.component.less'],
  standalone: false
})
export class InvoiceEmailModalComponent implements OnInit, OnChanges {
  @ViewChild('emailForm') form!: NgForm;

  visible = false;
  action: string = '';
  showPreview: boolean = false;

  link: any = null;
  data: any = {
    to: '',
    cc: '',
    bcc: '',
    ccList: [],
    ccOptions: [],
    bccList: [],
    primaryEmail: '',
    companyWebsite: ''
  };
  filteredEmailSuggestions: string[] = [];
  invoice: any = {};
  companyLogo: string =
    'https://portalvhdsgknxmwfdh5w3q.blob.core.windows.net/download/accounting/QCA_OCR.png';

  reportFormats: any[] = [];

  selectedReport: any = null;

  subjectLength: number = 0;
  contentLength: number = 0;
  isBccVisible: boolean = false;
  isValidEmail: boolean = true;
  showError: boolean = false;

  @Input() reportTypes: any[] = [];
  @Input() einvoiceControl: number = 0;
  @Output() readonly complete: EventEmitter<any> = new EventEmitter<any>();

  settings: any = {};
  errors: any = {};

  constructor(
    private settingSrv: SettingsService,
    private http: _HttpClient,
    private currency: CurrencyPipe,
    private acl: ACLService,
    public fs: FormService,
    public notificationSrv: NzNotificationService,
    private msg: NzMessageService,
    private reporting: ReportingService,
    private cdr: ChangeDetectorRef
  ) {
    this.settings = this.settingSrv.getUser();
  }
  toggleBccField() {
    this.isBccVisible = !this.isBccVisible;
  }

  validateEmails(emails: string): boolean {
    if (!emails) return true; // If field is empty, consider it valid (except for Send To)
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(emails.trim());
  }

  validateEmailArray(emails: string[]): boolean {
    if (!emails || emails.length === 0) return true; // Empty array is valid
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emails.every(email => emailPattern.test(email.trim()));
  }

  // Check if a specific field has invalid emails
  hasInvalidEmails(fieldName: string): boolean {
    const emails = this.data[fieldName];
    if (fieldName === 'ccList') {
      return !this.validateEmailArray(emails);
    }
    return emails ? !this.validateEmails(emails) : false;
  }

  onEmailInput(event: Event, fieldName: string) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.data[fieldName] = inputValue;

    // Update filtered suggestions for autocomplete
    if (fieldName === 'to') {
      this.updateFilteredEmailSuggestions(inputValue);
    }
  }

  shouldShowPrimaryEmail(): boolean {
    if (!this.data.primaryEmail) return false;

    const currentInput = this.data.to || '';
    if (!currentInput) return true;

    return this.data.primaryEmail
      .toLowerCase()
      .includes(currentInput.toLowerCase());
  }

  private updateFilteredEmailSuggestions(inputValue: string) {
    if (!this.data.ccOptions) {
      this.filteredEmailSuggestions = [];
      return;
    }

    if (!inputValue) {
      this.filteredEmailSuggestions = [...this.data.ccOptions];
      return;
    }

    this.filteredEmailSuggestions = this.data.ccOptions.filter(
      (email: string) => email.toLowerCase().includes(inputValue.toLowerCase())
    );
  }

  private hasValidEmails(emailList: string[]): boolean {
    if (!emailList || emailList.length === 0) return false;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailList.every(email => emailPattern.test(email.trim()));
  }

  ngOnInit(): void {
    this.loadCompanyLogo();
    this.loadReportFormats();
    return;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportTypes']) {
      this.loadReportFormats();
    }
  }

  show(data: any, option: any) {
    // Prevent unintentional context from opening
    if (!data || (data.docType != 'SINV' && data.docType != 'INV')) return;

    this.invoice = data;
    const request = {
      option: option,
      docId: this.invoice.id,
      docType: this.invoice.docType,
      amount: this.invoice.netTotalAmount,
      transactionType: this.selectedReport, // Store selected report format
      userPrimaryDb: true
    };

    // Ensure report formats are loaded before showing modal
    this.loadReportFormats().then(() => {
      this.http
        .post('api/PaymentPortal/GenerateLink', request, { _silent: true })
        .subscribe((res: any) => {
          this.data = {
            ...res,
            link: this.generateLink(res.linkId),
            to: res.to || '',
            ccList: res.ccEmails || [],
            ccOptions: res.ccEmails || [], // Use the same list for options
            bccList: [],
            primaryEmail: res.to || '', // Set primary email
            companyWebsite: ''
          };
          // Initialize filtered email suggestions
          this.filteredEmailSuggestions = this.data.ccOptions
            ? [...this.data.ccOptions]
            : [];
          if (res.attachmentType) this.selectedReport = res.attachmentType;
          this.textChanged();
          this.visible = true;
          this.showPreview = option === 'email';
          this.action = option;
          // Use setTimeout to ensure modal is rendered before change detection
          setTimeout(() => {
            this.cdr.detectChanges();
            // Trigger another change detection after a short delay to ensure all bindings are updated
            setTimeout(() => {
              this.cdr.detectChanges();
            }, 50);
          }, 0);
        });
    }).catch(() => {
      // If loadReportFormats fails, still show the modal but formats may not be loaded
      this.http
        .post('api/PaymentPortal/GenerateLink', request, { _silent: true })
        .subscribe((res: any) => {
          this.data = {
            ...res,
            link: this.generateLink(res.linkId),
            to: res.to || '',
            ccList: res.ccEmails || [],
            ccOptions: res.ccEmails || [], // Use the same list for options
            bccList: [],
            primaryEmail: res.to || '', // Set primary email
            companyWebsite: ''
          };
          // Initialize filtered email suggestions
          this.filteredEmailSuggestions = this.data.ccOptions
            ? [...this.data.ccOptions]
            : [];
          if (res.attachmentType) this.selectedReport = res.attachmentType;
          this.textChanged();
          this.visible = true;
          this.showPreview = option === 'email';
          this.action = option;
          setTimeout(() => {
            this.cdr.detectChanges();
            // Trigger another change detection after a short delay to ensure all bindings are updated
            setTimeout(() => {
              this.cdr.detectChanges();
            }, 50);
          }, 0);
        });
    });
  }

  updateCharacterCounts() {
    this.subjectLength = this.data.subject ? this.data.subject.length : 0;
    this.contentLength = this.data.message ? this.data.message.length : 0;
  }

  concatString(stringValues: string[]) {
    return stringValues.filter(x => x).join(' ');
  }

  textChanged() {
    const text = this.data.message
      .replaceAll('[ContactName]', this.data.contactName)
      .replaceAll(
        '[NetTotal]',
        this.currency.transform(this.data.netTotal, ' ')
      )
      .replaceAll('[DueDate]', this.data.formattedDueDate)
      .replaceAll('[CompanyName]', this.data.companyName)
      .replaceAll(
        '[InvoiceLink]',
        `<a target="_blank" href="${this.data.link}">${this.data.link}</a>`
      )
      .replaceAll('[DocDate]', this.data.formattedDocDate)
      .replaceAll('[CustomerName]', this.data.customerName)
      .replaceAll('\n\n', '</div><br><div>')
      .replaceAll('\n', '</div><div>');

    this.data.preview = `<div>${text}</div>`;

    this.updateCharacterCounts();
  }

  generateShareLink() {
    const validForm = this.fs.validate(this.form).isValid;
    if (!validForm) return;

    const request = {
      ...this.data,
      attachmentType: this.selectedReport,
      reportUrl: this.selectedReport,
      filter: JSON.stringify({ selection: [this.invoice.docCode] }),
      userPrimaryDb: true,
      type: this.getSendOption(),
      message: this.data.message
        .replaceAll('[ContactName]', this.data.contactName)
        .replaceAll(
          '[NetTotal]',
          this.currency.transform(this.data.netTotal, ' ')
        )
        .replaceAll('[DueDate]', this.data.formattedDueDate)
        .replaceAll('[CompanyName]', this.data.companyName)
        .replaceAll('[InvoiceLink]', this.data.link)
    };

    this.visible = false;
    const notification = this.notificationSrv.create(
      'info',
      'Generating link...',
      'Please wait',
      { nzDuration: 0 }
    );

    this.http
      .post(
        this.getFullUrl(`/api/InvoicePaymentShare/GenerateShareLink?_silent=true`),
        request
      )
      .subscribe((res: any) => {
        this.notificationSrv.remove(notification.messageId);
        switch (this.action) {
          case 'whatsapp':
          case 'telegram':
            window.open(res, '_blank');
            break;
          default:
            this.copyToClipboard(res);
            break;
        }
      });
  }

  copyToClipboard(data: any) {
    navigator.clipboard.writeText(data).then(() => {
      this.msg.success('Copied to clipboard');
    });
  }

  sendEmail() {
    if (!this.fs.validate(this.form).isValid || this.errors.message) {
      return;
    }

    var el = document.getElementById('email-preview');
    const request = {
      ...this.data,
      attachmentType: this.selectedReport,
      htmlContent: el?.innerHTML,
      reportUrl: this.selectedReport,
      filter: JSON.stringify({ selection: [this.invoice.docCode] }),
      userPrimaryDb: true,
      to: this.data.to,
      cc: this.data.ccList.join(','),
      bcc: this.data.bccList.join(',')
    };

    this.visible = false;
    const notification = this.notificationSrv.create(
      'info',
      'Sending email...',
      'Please wait',
      { nzDuration: 0 }
    );

    this.http
      .post('api/EmailSender/SendPaymentInvoice', request)
      .subscribe((res: any) => {
        this.notificationSrv.remove(notification.messageId);
      });
  }

  private loadReportFormats() {
    return new Promise((resolve, reject) => {
      // If report formats are already loaded and selectedReport is set, resolve immediately
      if (this.selectedReport && this.reportFormats.length > 0) {
        resolve(true);
        return;
      }
      if (!this.reportTypes) {
        reject('No report types found');
        return;
      }

      const allowedReports = this.reportTypes.filter(x =>
        x.acl ? this.acl.can(x.acl) : this.acl.can(`${x.reportType}.view`)
      );
      if (!allowedReports?.length) {
        reject('No report types found');
        return;
      }

      // Retrieve all format types into array of string
      const formatTypes = allowedReports.map(x => x.reportType).join(',');

      // Reset report formats
      this.reportFormats = [];

      this.http
        .get(
          `${this.reporting.reportServerMirrorUrl}/api/ReportFiles/GetByType`,
          {
            reportType: formatTypes
          }
        )
        .subscribe({
          next: (res: any[]) => {
            if (res.length > 0) {
              // Filter report formats based on einvoiceControl
              this.reportFormats = this.einvoiceControl === 1 || this.einvoiceControl === 2
                ? res.filter(r => r.isEInvFormat)
                : res.filter(r => !r.isEInvFormat);
              
              // Check if reportFormats has items before accessing first element
              if (this.reportFormats.length > 0 && !this.selectedReport) {
                this.selectedReport = this.reportFormats[0].reportUrl;
              }
              
              // Use setTimeout to ensure DOM is ready, but reduce delay
              setTimeout(() => {

                // reorder default report
                if (this.isAllowedReportType(formatTypes)) {
                  const transactionType = this.getReportType(
                    formatTypes
                  );

                  // Get the EInvDefault data for einvControl
                  this.http
                    .get('api/TenantData/EInvDefault')
                    .subscribe((eInvData: any[]) => {
                      // Reorder report formats based on einvControl
                      const defaultReport = eInvData ? this.reorderReportFormats(
                        eInvData,
                        this.einvoiceControl,
                        transactionType
                      ) : null;

                      if (defaultReport) {
                        // Move defaultReport to the start of res
                        this.reportFormats = [
                          ...this.reportFormats.filter(r => r.reportUrl === defaultReport),
                          ...this.reportFormats.filter(r => r.reportUrl !== defaultReport)
                        ];
                        this.selectedReport = this.reportFormats[0].reportUrl;
                      }
                      else {
                        this.reportFormats = [...this.reportFormats];
                        this.selectedReport = this.reportFormats[0].reportUrl;
                      }
                      // Trigger change detection after setting selectedReport
                      this.cdr.detectChanges();
                      resolve(true);
                    }, (error) => {
                      console.error('Error loading EInvDefault data:', error);
                      // Trigger change detection even on error
                      this.cdr.detectChanges();
                      resolve(true); // Still resolve as we have reportFormats
                    });
                } else {
                  // Trigger change detection even if not reordering
                  this.cdr.detectChanges();
                  resolve(true);
                }
              }, 100); // Reduced delay from 2000ms to 100ms
            } else {
              // No results from API
              this.cdr.detectChanges();
              resolve(true);
            }
          },
          error: (error) => {
            console.error('Error loading report formats:', error);
            reject(error);
          }
        });
    });
  }

  private isAllowedReportType(reportType: string): boolean {
    // List of allowed report types
    const allowedReportTypes = [
      'sinv-voucher',
      'sdn-voucher',
      'scn-voucher',
      'cs-voucher',
      'pinv-voucher',
      'pdn-voucher',
      'pcn-voucher',
      'cp-voucher'
    ];

    // Check if reportType is in the allowed list
    return allowedReportTypes.includes(reportType);
  }

  getReportType(reportTypes: string) {
    switch (reportTypes) {
      case 'sinv-voucher':
        return 'SINV';
      case 'sdn-voucher':
        return 'SDN';
      case 'scn-voucher':
        return 'SCN';
      case 'cs-voucher':
        return 'CS';
      case 'pinv-voucher':
        return 'PINV';
      case 'pdn-voucher':
        return 'PDN';
      case 'pcn-voucher':
        return 'PCN';
      case 'cp-voucher':
        return 'CP';
      default:
        return '';
    }
  }  
  
  private reorderReportFormats(
    eInvData: any[],
    einvControl: number,
    transactionType: string
  ) {
    const einvControlMap: { [key: number]: string } = {
      0: 'draft',
      1: 'portal',
      2: 'full',
      3: 'exempted'
    };

    const formatKey = einvControlMap[einvControl]; // Get the format key based on einvControl

    if (transactionType && formatKey) {
      const transaction = eInvData.find(
        t => t.transactionType === transactionType
      );
      return transaction ? transaction[formatKey] : null; // Returns the full repx string or null if not found
    }
    return null;
  }

  private getFullUrl(url: string) {
    let serverUrl = this.reporting.reportServerUrl;

    if (!serverUrl.endsWith('/')) serverUrl = `${serverUrl}/`;

    url =
      serverUrl +
      (serverUrl.endsWith('/') && url.startsWith('/') ? url.substring(1) : url);

    return url;
  }

  private generateLink(id: string) {
    return `${window.location.origin}/#/full/portal/pay/${id}`;
  }

  private getSendOption() {
    switch (this.action) {
      case 'email':
        return 1;
      case 'whatsapp':
        return 2;
      case 'telegram':
        return 3;
      default:
        return 4;
    }
  }

  loadCompanyLogo() {
    this.http.get('api/companyprofile/logo').subscribe(res => {
      if (res && res.companyImage !== null) this.companyLogo = this.normalizeImageUrl(res.companyImage);
    });
  }

  private normalizeImageUrl(url?: string): string {
    if (!url) return this.companyLogo;
    return url.replace(/ /g, '%20');
  }

  onEmailSelectionChange(selectedEmail: string) {
    this.data.receipientEmail = selectedEmail;
  }

  isEmailDisabled(email: string, fieldName: string): boolean {
    if (fieldName === 'to') {
      // For Send To field, disable emails that are already in CC
      return this.data.ccList && this.data.ccList.includes(email);
    } else if (fieldName === 'ccList') {
      // For CC field, disable email that is already selected in Send To
      return this.data.to === email;
    }
    return false;
  }
}
